import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ChallengePage.module.css";
import { open_api_with_cookies } from "../../Utils/network";
import { Helmet } from "react-helmet";
import CheckpointDetailModal from "./CheckpointDetailModal";
import {
  formatCheckpointDate,
  getCheckpointIcon,
  safeParseHTML,
} from "./challengeUtils";

// Toast notification component
const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✅";
      case "error":
        return "❌";
      case "info":
        return "ℹ️";
      default:
        return "✅";
    }
  };

  return (
    <div className={`${styles.toast} ${styles[type]}`}>
      <span className={styles.toastIcon}>{getIcon()}</span>
      <span className={styles.toastMessage}>{message}</span>
      <button className={styles.toastClose} onClick={onClose}>
        ×
      </button>
    </div>
  );
};

// Toast provider component
const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <div>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

// Loading skeleton component
const ProgressSkeleton = () => (
  <div className={styles.progressSkeleton}>
    <div className={styles.skeletonHero}></div>
    <div className={styles.skeletonStats}></div>
    <div className={styles.skeletonTabs}></div>
    <div className={styles.skeletonContent}></div>
  </div>
);

// Feed post skeleton
const FeedPostSkeleton = () => (
  <div className={styles.feedPostSkeleton}>
    <div className={styles.skeletonAvatar}></div>
    <div className={styles.skeletonContent}>
      <div className={styles.skeletonHeader}></div>
      <div className={styles.skeletonText}></div>
      <div className={styles.skeletonActions}></div>
    </div>
  </div>
);

// Participant card skeleton
const ParticipantSkeleton = () => (
  <div className={styles.participantSkeleton}>
    <div className={styles.skeletonAvatar}></div>
    <div className={styles.skeletonContent}>
      <div className={styles.skeletonHeader}></div>
      <div className={styles.skeletonStats}></div>
      <div className={styles.skeletonProgress}></div>
    </div>
  </div>
);

// Participant card component
const ParticipantCard = ({ participant, index }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const formatJoinDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Joined today";
    if (diffInDays === 1) return "Joined yesterday";
    if (diffInDays < 7) return `Joined ${diffInDays} days ago`;
    if (diffInDays < 30)
      return `Joined ${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) !== 1 ? "s" : ""} ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 3: // Paid
        return styles.statusPaid;
      case 2: // Pending
        return styles.statusPending;
      case 1: // Failed
        return styles.statusFailed;
      default:
        return styles.statusDefault;
    }
  };

  const getCompletionLevel = (percentage) => {
    if (percentage === 0) return { level: "Just Started", color: "#64748b" };
    if (percentage < 25) return { level: "Getting Started", color: "#f59e0b" };
    if (percentage < 50) return { level: "Making Progress", color: "#3b82f6" };
    if (percentage < 75) return { level: "Almost There", color: "#8b5cf6" };
    if (percentage < 100) return { level: "Nearly Done", color: "#10b981" };
    return { level: "Challenge Complete!", color: "#22c55e" };
  };

  const completionInfo = getCompletionLevel(
    participant.completion_stats.completion_percentage
  );

  return (
    <div
      ref={cardRef}
      className={styles.participantCard}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={styles.participantHeader}>
        <div className={styles.participantAvatar}>
          {participant.user_profile?.profile_picture ? (
            <img
              src={participant.user_profile?.profile_picture}
              alt={participant.user_name}
              className={styles.avatarImage}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {getInitials(participant.user_name)}
            </div>
          )}
        </div>

        <div className={styles.participantInfo}>
          <h4 className={styles.participantName}>{participant.user_name}</h4>
          <div className={styles.participantMeta}>
            <span className={styles.joinDate}>
              {formatJoinDate(participant.join_date)}
            </span>
            {/* <span
              className={`${styles.paymentStatus} ${getPaymentStatusColor(participant.payment_status)}`}
            >
              {participant.payment_status_display}
            </span> */}
          </div>
        </div>

        {/* <div
          className={styles.completionBadge}
          style={{ background: completionInfo.color }}
        >
          {Math.round(participant.completion_stats.completion_percentage)}%
        </div> */}
      </div>

      <div className={styles.participantStats}>
        <div className={styles.statItem}>
          <span className={styles.statNumberParticipant}>
            {participant.completion_stats.completed_checkpoints}
          </span>
          <span className={styles.statLabelParticipant}>Completed</span>
        </div>

        <div className={styles.statDivider}></div>

        <div className={styles.statItem}>
          <span className={styles.statNumberParticipant}>
            {participant.completion_stats.total_checkpoints}
          </span>
          <span className={styles.statLabelParticipant}>Total</span>
        </div>

        <div className={styles.statDivider}></div>

        <div className={styles.statItem}>
          <span
            className={styles.statLevel}
            style={{ color: completionInfo.color }}
          >
            {completionInfo.level}
          </span>
        </div>
      </div>

      <div className={styles.participantProgress}>
        <div className={styles.progressLabel}>
          <span>Progress</span>
          <span>
            {Math.round(participant.completion_stats.completion_percentage)}%
          </span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{
              width: `${participant.completion_stats.completion_percentage}%`,
              background: completionInfo.color,
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Comment component
const Comment = ({ comment, onEdit, onDelete, currentUserId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.comment);
  const [isLoading, setIsLoading] = useState(false);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleEdit = async () => {
    if (editText.trim() === comment.comment.trim()) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await onEdit(comment.id, editText.trim());
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to edit comment:", error);
      setEditText(comment.comment); // Reset to original text
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditText(comment.comment);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      setIsLoading(true);
      try {
        await onDelete(comment.id);
      } catch (error) {
        console.error("Failed to delete comment:", error);
        setIsLoading(false);
      }
    }
  };

  const canModify = currentUserId && currentUserId === comment.user_id;

  return (
    <div className={`${styles.comment} ${isLoading ? styles.loading : ""}`}>
      <div className={styles.commentHeader}>
        <div className={styles.commentUser}>
          <strong>{comment.user_name}</strong>
          <span className={styles.commentTime}>
            {formatTimeAgo(comment.created)}
          </span>
        </div>
        {canModify && !isEditing && (
          <div className={styles.commentActions}>
            <button
              className={styles.editCommentButton}
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              title="Edit comment"
            >
              ✏️
            </button>
            <button
              className={styles.deleteCommentButton}
              onClick={handleDelete}
              disabled={isLoading}
              title="Delete comment"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className={styles.commentEditForm}>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className={styles.commentEditInput}
            rows={3}
            maxLength={500}
            disabled={isLoading}
          />
          <div className={styles.commentEditActions}>
            <button
              className={styles.saveCommentButton}
              onClick={handleEdit}
              disabled={isLoading || !editText.trim()}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
            <button
              className={styles.cancelCommentButton}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className={styles.commentText}>{comment.comment}</p>
      )}
    </div>
  );
};

// Add comment form component
const AddCommentForm = ({ onSubmit, isLoading }) => {
  const [comment, setComment] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    try {
      await onSubmit(comment.trim());
      setComment("");
    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  return (
    <form className={styles.addCommentForm} onSubmit={handleSubmit}>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Write a comment..."
        className={styles.commentInput}
        rows={2}
        maxLength={500}
        disabled={isLoading}
      />
      <div className={styles.commentFormActions}>
        <div className={styles.commentCharCount}>{comment.length}/500</div>
        <button
          type="submit"
          className={styles.submitCommentButton}
          disabled={isLoading || !comment.trim()}
        >
          {isLoading ? "Posting..." : "Post Comment"}
        </button>
      </div>
    </form>
  );
};

// Reaction selector component
const ReactionSelector = ({ onReactionSelect, position = "top" }) => {
  const reactions = [
    { type: "heart", emoji: "❤️", label: "Love" },
    { type: "fire", emoji: "🔥", label: "Fire" },
    { type: "clap", emoji: "👏", label: "Clap" },
    { type: "thumbsup", emoji: "👍", label: "Like" },
    { type: "celebrate", emoji: "🎉", label: "Celebrate" },
    { type: "love", emoji: "😍", label: "Amazing" },
  ];

  return (
    <div className={`${styles.reactionSelector} ${styles[position]}`}>
      {reactions.map((reaction) => (
        <button
          key={reaction.type}
          className={styles.reactionSelectorButton}
          onClick={() => onReactionSelect(reaction.type)}
          title={reaction.label}
        >
          {reaction.emoji}
        </button>
      ))}
    </div>
  );
};

// Reaction component
const ReactionButton = ({
  reaction,
  count,
  isActive,
  onClick,
  isLoading = false,
}) => {
  const getReactionEmoji = (type) => {
    const reactions = {
      heart: "❤️",
      fire: "🔥",
      clap: "👏",
      thumbsup: "👍",
      celebrate: "🎉",
      love: "😍",
    };
    return reactions[type] || "👍";
  };

  return (
    <button
      className={`${styles.reactionButton} ${isActive ? styles.active : ""} ${isLoading ? styles.loading : ""}`}
      onClick={onClick}
      disabled={isLoading}
    >
      <span className={styles.reactionEmoji}>{getReactionEmoji(reaction)}</span>
      {count > 0 && <span className={styles.reactionCount}>{count}</span>}
    </button>
  );
};

// Image lightbox component
const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  const lightboxRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (lightboxRef.current && e.target === lightboxRef.current) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("click", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.lightboxOverlay} ref={lightboxRef}>
      <div className={styles.lightboxContainer}>
        <button className={styles.lightboxClose} onClick={onClose}>
          ×
        </button>
        <img src={src} alt={alt} className={styles.lightboxImage} />
        <div className={styles.lightboxActions}>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.lightboxView}
            onClick={(e) => e.stopPropagation()}
          >
            🔗 Open in new tab
          </a>
        </div>
      </div>
    </div>
  );
};

// File attachment component
const FileAttachment = ({ attachment, fileName, onDownload }) => {
  const getFileIcon = (fileName) => {
    if (!fileName) return "📄";

    const extension = fileName.split(".").pop()?.toLowerCase();
    const iconMap = {
      pdf: "📄",
      doc: "📝",
      docx: "📝",
      txt: "📝",
      rtf: "📝",
      xls: "📊",
      xlsx: "📊",
      csv: "📊",
      ppt: "📊",
      pptx: "📊",
      zip: "🗜️",
      rar: "🗜️",
      "7z": "🗜️",
      mp3: "🎵",
      wav: "🎵",
      mp4: "🎥",
      avi: "🎥",
      mov: "🎥",
      mkv: "🎥",
    };

    return iconMap[extension] || "📄";
  };

  const getFileSize = async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const contentLength = response.headers.get("content-length");
      if (contentLength) {
        const bytes = parseInt(contentLength, 10);
        const mb = (bytes / (1024 * 1024)).toFixed(2);
        return `${mb} MB`;
      }
    } catch (error) {
      console.error("Error getting file size:", error);
    }
    return "";
  };

  const [fileSize, setFileSize] = useState("");

  useEffect(() => {
    getFileSize(attachment).then(setFileSize);
  }, [attachment]);

  const extractFileName = () => {
    if (fileName) return fileName;

    try {
      const url = new URL(attachment);
      const pathParts = url.pathname.split("/");
      const lastPart = pathParts[pathParts.length - 1];
      return decodeURIComponent(lastPart) || "attachment";
    } catch {
      return "attachment";
    }
  };

  const displayFileName = extractFileName();

  return (
    <div className={styles.fileAttachment}>
      <div className={styles.fileAttachmentHeader}>
        <div className={styles.fileAttachmentIcon}>
          {getFileIcon(displayFileName)}
        </div>
        <div className={styles.fileAttachmentInfo}>
          <div className={styles.fileAttachmentName}>{displayFileName}</div>
          {fileSize && (
            <div className={styles.fileAttachmentSize}>{fileSize}</div>
          )}
        </div>
      </div>
      <div className={styles.fileAttachmentActions}>
        <a
          href={attachment}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.fileAttachmentButton}
          onClick={(e) => e.stopPropagation()}
        >
          👁️ View
        </a>
      </div>
    </div>
  );
};

// Enhanced attachment component that handles both images and files
const PostAttachment = ({ attachment, fileName }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [attachmentType, setAttachmentType] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const determineAttachmentType = () => {
      if (!attachment) return;

      // First, try to determine from file extension if fileName is provided
      if (fileName) {
        const extension = fileName.split(".").pop()?.toLowerCase();
        const imageExtensions = [
          "jpg",
          "jpeg",
          "png",
          "gif",
          "webp",
          "svg",
          "bmp",
        ];
        if (imageExtensions.includes(extension)) {
          setAttachmentType("image");
          return;
        } else {
          setAttachmentType("file");
          return;
        }
      }

      // Fallback: try to determine from URL
      try {
        const url = new URL(attachment);
        const pathname = url.pathname.toLowerCase();
        const imageExtensions = [
          ".jpg",
          ".jpeg",
          ".png",
          ".gif",
          ".webp",
          ".svg",
          ".bmp",
        ];

        if (imageExtensions.some((ext) => pathname.endsWith(ext))) {
          setAttachmentType("image");
        } else {
          setAttachmentType("file");
        }
      } catch {
        // If URL parsing fails, try to load as image first
        const img = new Image();
        img.onload = () => setAttachmentType("image");
        img.onerror = () => setAttachmentType("file");
        img.src = attachment;
      }
    };

    determineAttachmentType();
  }, [attachment, fileName]);

  const handleImageError = () => {
    setImageError(true);
    setAttachmentType("file");
  };

  const handleImageClick = () => {
    if (attachmentType === "image" && !imageError) {
      setLightboxOpen(true);
    }
  };

  if (!attachment) return null;

  return (
    <div className={styles.postAttachment}>
      {attachmentType === "image" && !imageError ? (
        <>
          <div className={styles.imageAttachmentContainer}>
            <img
              src={attachment}
              alt="Post attachment"
              className={styles.attachmentImage}
              onClick={handleImageClick}
              onError={handleImageError}
            />
            <div className={styles.imageAttachmentOverlay}>
              <button
                className={styles.imageAttachmentButton}
                onClick={handleImageClick}
                title="Click to expand"
              >
                🔍 View full size
              </button>
            </div>
          </div>
          <ImageLightbox
            src={attachment}
            alt="Post attachment"
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        </>
      ) : (
        <FileAttachment attachment={attachment} fileName={fileName} />
      )}
    </div>
  );
};

// Feed post component
const FeedPost = ({ post, index, onUpdatePost, currentUserId, showToast }) => {
  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReactionSelector, setShowReactionSelector] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [localPost, setLocalPost] = useState(post);
  const postRef = useRef(null);
  const reactionSelectorRef = useRef(null);

  useEffect(() => {
    setLocalPost(post);
  }, [post]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (postRef.current) {
      observer.observe(postRef.current);
    }

    return () => {
      if (postRef.current) {
        observer.unobserve(postRef.current);
      }
    };
  }, []);

  // Close reaction selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reactionSelectorRef.current &&
        !reactionSelectorRef.current.contains(event.target)
      ) {
        setShowReactionSelector(false);
      }
    };

    if (showReactionSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showReactionSelector]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const shouldTruncate = localPost.text.length > 150;
  const displayText =
    shouldTruncate && !isExpanded
      ? localPost.text.substring(0, 150) + "..."
      : localPost.text;

  // Group reactions by type and check if current user has reacted
  const groupedReactions = localPost.reactions.reduce((acc, reaction) => {
    if (!acc[reaction.reaction]) {
      acc[reaction.reaction] = {
        count: 0,
        userReacted: false,
      };
    }
    acc[reaction.reaction].count++;
    if (currentUserId && reaction.user_id === currentUserId) {
      acc[reaction.reaction].userReacted = true;
    }
    return acc;
  }, {});

  // Handle reaction
  const handleReaction = async (reactionType) => {
    if (!currentUserId) {
      showToast && showToast("Please log in to react to posts", "error");
      return;
    }

    const currentReaction = groupedReactions[reactionType];
    const isRemoving = currentReaction?.userReacted;

    setReactionLoading(reactionType);
    setShowReactionSelector(false);

    try {
      if (isRemoving) {
        // Remove reaction
        await open_api_with_cookies.delete(
          `challenge/submission/${localPost.id}/react/`
        );
        showToast && showToast("Reaction removed", "info");
      } else {
        // Add reaction
        await open_api_with_cookies.post(
          `challenge/submission/${localPost.id}/react/`,
          {
            reaction: reactionType,
          }
        );
        showToast && showToast("Reaction added! 🎉", "success");
      }

      // Update local state optimistically
      const updatedReactions = [...localPost.reactions];

      if (isRemoving) {
        // Remove user's reaction of this specific type
        const reactionIndex = updatedReactions.findIndex(
          (r) => r.reaction === reactionType && r.user_id === currentUserId
        );
        if (reactionIndex > -1) {
          updatedReactions.splice(reactionIndex, 1);
        }
      } else {
        // First, remove any existing reaction from this user (to prevent multiple reactions)
        const existingIndex = updatedReactions.findIndex(
          (r) => r.user_id === currentUserId
        );
        if (existingIndex > -1) {
          updatedReactions.splice(existingIndex, 1);
        }

        // Add the new reaction
        updatedReactions.push({
          reaction: reactionType,
          user_id: currentUserId,
        });
      }

      const updatedPost = {
        ...localPost,
        reactions: updatedReactions,
      };

      setLocalPost(updatedPost);
      onUpdatePost(updatedPost);
    } catch (error) {
      console.error("Failed to update reaction:", error);
      showToast &&
        showToast("Failed to update reaction. Please try again.", "error");
    } finally {
      setReactionLoading(null);
    }
  };

  // Handle add comment
  const handleAddComment = async (commentText) => {
    if (!currentUserId) {
      showToast && showToast("Please log in to comment", "error");
      return;
    }

    setCommentLoading(true);
    try {
      const response = await open_api_with_cookies.post(
        `challenge/submission/${localPost.id}/comment/`,
        { comment: commentText }
      );

      if (response.status === 201) {
        const newComment = response.data;
        const updatedPost = {
          ...localPost,
          comments: [...localPost.comments, newComment],
          comments_count: localPost.comments_count + 1,
        };

        setLocalPost(updatedPost);
        onUpdatePost(updatedPost);
        showToast && showToast("Comment added successfully! 💬", "success");
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
      showToast &&
        showToast("Failed to add comment. Please try again.", "error");
      throw error;
    } finally {
      setCommentLoading(false);
    }
  };

  // Handle edit comment
  const handleEditComment = async (commentId, newText) => {
    try {
      const response = await open_api_with_cookies.put(
        `challenge/submission/${localPost.id}/comment/`,
        {
          comment_id: commentId,
          comment: newText,
        }
      );

      if (response.status === 200) {
        const updatedComments = localPost.comments.map((comment) =>
          comment.id === commentId ? { ...comment, comment: newText } : comment
        );

        const updatedPost = {
          ...localPost,
          comments: updatedComments,
        };

        setLocalPost(updatedPost);
        onUpdatePost(updatedPost);
        showToast && showToast("Comment updated successfully! ✏️", "success");
      }
    } catch (error) {
      console.error("Failed to edit comment:", error);
      showToast &&
        showToast("Failed to update comment. Please try again.", "error");
      throw error;
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    try {
      await open_api_with_cookies.delete(
        `challenge/submission/${localPost.id}/comment/`,
        {
          data: { comment_id: commentId },
        }
      );

      const updatedComments = localPost.comments.filter(
        (comment) => comment.id !== commentId
      );
      const updatedPost = {
        ...localPost,
        comments: updatedComments,
        comments_count: localPost.comments_count - 1,
      };

      setLocalPost(updatedPost);
      onUpdatePost(updatedPost);
      showToast && showToast("Comment deleted", "info");
    } catch (error) {
      console.error("Failed to delete comment:", error);
      showToast &&
        showToast("Failed to delete comment. Please try again.", "error");
      throw error;
    }
  };

  return (
    <div
      ref={postRef}
      className={`${styles.feedPost} ${localPost.is_anonymous ? styles.anonymousPost : ""}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={styles.postHeader}>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            {localPost.user_profile?.profile_picture ? (
              <img
                src={localPost.user_profile?.profile_picture}
                alt={localPost.user_name}
                className={styles.avatarImage}
              />
            ) : (
              <div
                className={`${styles.avatarPlaceholder} ${localPost.is_anonymous ? styles.anonymousAvatar : ""}`}
              >
                {localPost.is_anonymous
                  ? "?"
                  : getInitials(localPost.user_name)}
              </div>
            )}
          </div>
          <div className={styles.userDetails}>
            <h4
              className={`${styles.userName} ${localPost.is_anonymous ? styles.anonymousName : ""}`}
            >
              {localPost.user_name}
              {localPost.is_anonymous && (
                <span className={styles.anonymousBadge}>👤</span>
              )}
            </h4>
            <div className={styles.postMeta}>
              <span className={styles.checkpointTag}>
                Day {localPost.checkpoint_info.checkpoint_index}:{" "}
                {localPost.checkpoint_info.checkpoint_title}
              </span>
              <span className={styles.timeAgo}>
                {formatTimeAgo(localPost.created)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.postContent}>
        <p className={styles.postText}>
          {displayText}
          {shouldTruncate && (
            <button
              className={styles.expandButton}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? " Show less" : " Read more"}
            </button>
          )}
        </p>

        {localPost.attachment && (
          <PostAttachment
            attachment={localPost.attachment}
            fileName={localPost.attachment_name}
          />
        )}
      </div>

      <div className={styles.postActions}>
        <div className={styles.reactions}>
          {Object.entries(groupedReactions).map(
            ([reactionType, reactionData]) => (
              <ReactionButton
                key={reactionType}
                reaction={reactionType}
                count={reactionData.count}
                isActive={reactionData.userReacted}
                isLoading={reactionLoading === reactionType}
                onClick={() => handleReaction(reactionType)}
              />
            )
          )}

          <div
            className={styles.addReactionContainer}
            ref={reactionSelectorRef}
          >
            <button
              className={styles.addReactionButton}
              onClick={() => setShowReactionSelector(!showReactionSelector)}
              title="Add reaction"
            >
              <span>+</span>
            </button>
            {showReactionSelector && (
              <ReactionSelector
                onReactionSelect={handleReaction}
                position="top"
              />
            )}
          </div>
        </div>

        <div className={styles.actionButtons}>
          <button
            className={styles.commentButton}
            onClick={() => setShowComments(!showComments)}
          >
            💬 {localPost.comments_count}{" "}
            {localPost.comments_count === 1 ? "comment" : "comments"}
          </button>
        </div>
      </div>

      {showComments && (
        <div className={styles.commentsSection}>
          {localPost.comments.length > 0 && (
            <div className={styles.commentsList}>
              {localPost.comments.map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}

          <AddCommentForm
            onSubmit={handleAddComment}
            isLoading={commentLoading}
          />
        </div>
      )}
    </div>
  );
};

// Checkpoint card component
const CheckpointCard = ({ checkpoint, index, onStart }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const isLocked = checkpoint.status === "locked";
  const isCompleted = checkpoint.is_completed;

  return (
    <div
      ref={cardRef}
      className={`${styles.checkpointCard} ${isLocked ? styles.locked : ""} ${isCompleted ? styles.completed : ""}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className={styles.checkpointIcon}>
        {isCompleted ? (
          <div className={styles.completedIcon}>
            {getCheckpointIcon(true, false)}
          </div>
        ) : isLocked ? (
          <div className={styles.lockIcon}>
            {getCheckpointIcon(false, true)}
          </div>
        ) : (
          <div className={styles.availableIcon}>
            {getCheckpointIcon(false, false)}
          </div>
        )}
      </div>

      <div className={styles.checkpointContent}>
        <div className={styles.checkpointHeader}>
          <h3 className={styles.checkpointTitle}>
            Day {checkpoint.checkpoint_index}: {checkpoint.checkpoint_title}
          </h3>
          <span className={styles.checkpointDate}>
            {formatCheckpointDate(checkpoint.date)}
          </span>
        </div>

        {/* {checkpoint.title && (
          <div
            className={styles.checkpointSubtitle}
            dangerouslySetInnerHTML={safeParseHTML(checkpoint.title)}
          />
        )}

        {checkpoint.sub_title && (
          <div
            className={styles.checkpointSubContent}
            dangerouslySetInnerHTML={safeParseHTML(checkpoint.sub_title)}
          />
        )} */}
      </div>

      <div className={styles.checkpointAction}>
        <button
          className={`${styles.actionButton} ${isCompleted ? styles.completedButton : styles.startButton}`}
          onClick={() => onStart(checkpoint)}
          disabled={isLocked}
        >
          {isCompleted ? "Completed" : isLocked ? "Locked" : "Start"}
        </button>
      </div>
    </div>
  );
};

// Progress stats component
const ProgressStats = ({ data }) => (
  <div className={styles.progressStats}>
    <div className={styles.statCard}>
      <div className={styles.statNumber}>{data.completed_checkpoints}</div>
      <div className={styles.statLabel}>Completed</div>
    </div>
    <div className={styles.statDivider}></div>
    <div className={styles.statCard}>
      <div className={styles.statNumber}>
        {data.completed_checkpoints}/{data.total_checkpoints}
      </div>
      <div className={styles.statLabel}>Checkpoints</div>
    </div>
  </div>
);

// WhatsApp join component
const WhatsAppJoin = ({ link }) => {
  if (!link) return null;

  return (
    <div className={styles.whatsappJoinContainer}>
      <div className={styles.whatsappJoinHeader}>
        <div className={styles.whatsappLogo}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366">
            <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345z" />
            <path
              d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652c1.746.943 3.71 1.444 5.71 1.447h.006c9.756 0 17.683-7.887 17.682-17.444-.001-4.654-1.813-9.03-5.113-12.327zm-7.165 26.696h-.005c-2.646-.001-5.247-.667-7.533-1.923l-.54-.32-5.614 1.47 1.489-5.42-.35-.565c-1.484-2.343-2.272-5.03-2.27-7.75.004-8.105 6.595-14.705 14.701-14.705 3.923 0 7.608 1.531 10.377 4.3 2.767 2.766 4.293 6.453 4.291 10.386-.004 8.105-6.594 14.704-14.693 14.704z"
              fill-rule="nonzero"
            />
          </svg>
        </div>
        <span className={styles.whatsappJoinTitle}>Challenge Community</span>
      </div>
      <p className={styles.whatsappJoinDescription}>
        Join our WhatsApp group for daily motivation, support, and tips from
        coaches and fellow participants.
      </p>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.whatsappJoinButton}
      >
        <div className={styles.whatsappIcon}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="#FFFFFF">
            <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345z" />
          </svg>
        </div>
        <span className={styles.whatsappJoinText}>Join WhatsApp Group</span>
      </a>
    </div>
  );
};

// Hero section component
const HeroSection = ({ data }) => {
  const progressBarRef = useRef(null);

  useEffect(() => {
    if (progressBarRef.current && data?.progress_percentage !== undefined) {
      const timeoutId = setTimeout(() => {
        if (progressBarRef.current) {
          progressBarRef.current.style.width = `${data.progress_percentage}%`;
        }
      }, 500);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [data.progress_percentage]);

  return (
    <div className={styles.heroSection}>
      <div className={styles.heroContent}>
        <div className={styles.heroText}>
          <h1 className={styles.heroTitle}>{data.title}</h1>
          <p className={styles.heroSubtitle}>
            You are one decision away from your best self
          </p>
          <ProgressStats data={data} />
          <WhatsAppJoin link={data.whatsapp_group_link} />
        </div>

        <div className={styles.heroImage}>
          {data.hero_image && (
            <img
              src={data.hero_image}
              alt="Challenge transformation"
              className={styles.transformationImage}
              loading="lazy"
            />
          )}
          <div className={styles.challengeBadge}>
            <span className={styles.badgeText}>21-DAY WELLNESS CHALLENGE</span>
          </div>
        </div>
      </div>

      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div ref={progressBarRef} className={styles.progressFill}></div>
        </div>
        <span className={styles.progressText}>
          {Math.round(data.progress_percentage || 0)}% Complete
        </span>
      </div>
    </div>
  );
};

// Main component
const ChallengePage = () => {
  const {
    challengeSlug,
    checkpointSlug: urlCheckpointSlug,
    challengePurchaseId,
  } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challengeData, setChallengeData] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [feedPosts, setFeedPosts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [activeTab, setActiveTab] = useState("progress");
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [challengeBatch, setChallengeBatch] = useState(null);
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);

  // Feed pagination state
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedPage, setFeedPage] = useState(1);
  const [hasMoreFeed, setHasMoreFeed] = useState(true);
  const [feedError, setFeedError] = useState(null);
  const [feedInitialFetchAttempted, setFeedInitialFetchAttempted] =
    useState(false);

  // Participants pagination state
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [hasMoreParticipants, setHasMoreParticipants] = useState(true);
  const [participantsError, setParticipantsError] = useState(null);
  const [
    participantsInitialFetchAttempted,
    setParticipantsInitialFetchAttempted,
  ] = useState(false);

  // Toast functions
  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Get tab icon for mobile navigation
  const getTabIcon = (tab) => {
    const icons = {
      progress: "📊",
      feed: "💬",
      participants: "👥",
    };
    return icons[tab] || "📋";
  };

  // Fetch current user data
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await open_api_with_cookies.get("profiles/getUserId/");
      if (response.status === 200) {
        setCurrentUserId(response.data.user_id);
      }
    } catch (err) {
      console.error("Error fetching current user:", err);
    }
  }, []);

  // Handle feed post update
  const handleFeedPostUpdate = useCallback((updatedPost) => {
    setFeedPosts((prev) =>
      prev.map((post) => (post.id === updatedPost.id ? updatedPost : post))
    );
  }, []);

  // Fetch challenge batch data
  const fetchChallengeBatch = useCallback(async () => {
    try {
      const response = await open_api_with_cookies.get(
        `challenge/purchase/${challengePurchaseId}/batch/`
      );
      if (response.status === 200) {
        setChallengeBatch(response.data.batch_slug);
      }
    } catch (err) {
      console.error("Error fetching challenge batch:", err);
    }
  }, [challengePurchaseId]);

  // Fetch challenge progress data
  const fetchChallengeProgress = useCallback(async () => {
    try {
      const response = await open_api_with_cookies.get(
        `challenge/${challengeSlug}/batch/${challengeBatch}/progress/`
      );
      if (response.status === 200) {
        setChallengeData(response.data);
      }
    } catch (err) {
      console.error("Error fetching challenge progress:", err);
      if (err.response?.status === 404) {
        setError("Challenge not found");
      } else {
        setError("Failed to load challenge data");
      }
    }
  }, [challengeSlug, challengeBatch]);

  // Fetch checkpoints data
  const fetchCheckpoints = useCallback(async () => {
    try {
      const response = await open_api_with_cookies.get(
        `challenge/${challengeSlug}/batch/${challengeBatch}/checkpoints/`
      );
      if (response.status === 200) {
        setCheckpoints(response.data.results || []);
      }
    } catch (err) {
      console.error("Error fetching checkpoints:", err);
      setError("Failed to load checkpoints");
    }
  }, [challengeSlug, challengeBatch]);

  // Fetch feed data
  const fetchFeed = useCallback(
    async (page = 1, reset = false) => {
      try {
        setFeedLoading(true);
        setFeedError(null);

        console.log(`Fetching feed page ${page}, reset: ${reset}`);

        const response = await open_api_with_cookies.get(
          `challenge/${challengeSlug}/batch/${challengeBatch}/feed/?page=${page}`
        );

        console.log("Feed API response:", response.data);

        if (response.status === 200) {
          const newPosts = response.data.data || [];
          const hasNextPage = response.data.next_page !== null;

          console.log(
            `Received ${newPosts.length} posts, hasNextPage: ${hasNextPage}`
          );

          if (reset) {
            setFeedPosts(newPosts);
          } else {
            setFeedPosts((prev) => [...prev, ...newPosts]);
          }

          // Additional safety: if we got no posts and it's not the first page, assume no more data
          if (newPosts.length === 0 && page > 1) {
            console.log(
              "No posts received for page > 1, setting hasMoreFeed to false"
            );
            setHasMoreFeed(false);
          } else {
            setHasMoreFeed(hasNextPage);
          }

          setFeedPage(page);
        }
      } catch (err) {
        console.error("Error fetching feed:", err);
        setFeedError("Failed to load feed");
        // Reset hasMoreFeed on error to prevent infinite calls
        setHasMoreFeed(false);
      } finally {
        setFeedLoading(false);
      }
    },
    [challengeSlug, challengeBatch]
  );

  // Fetch participants data
  const fetchParticipants = useCallback(
    async (page = 1, reset = false) => {
      try {
        setParticipantsLoading(true);
        setParticipantsError(null);

        console.log(`Fetching participants page ${page}, reset: ${reset}`);

        const response = await open_api_with_cookies.get(
          `challenge/${challengeSlug}/batch/${challengeBatch}/participants/?page=${page}`
        );

        console.log("Participants API response:", response.data);

        if (response.status === 200) {
          const newParticipants = response.data.data || [];
          const hasNextPage = response.data.next_page !== null;

          console.log(
            `Received ${newParticipants.length} participants, hasNextPage: ${hasNextPage}`
          );

          if (reset) {
            setParticipants(newParticipants);
          } else {
            setParticipants((prev) => [...prev, ...newParticipants]);
          }

          // Additional safety: if we got no participants and it's not the first page, assume no more data
          if (newParticipants.length === 0 && page > 1) {
            console.log(
              "No participants received for page > 1, setting hasMoreParticipants to false"
            );
            setHasMoreParticipants(false);
          } else {
            setHasMoreParticipants(hasNextPage);
          }

          setParticipantsPage(page);
        }
      } catch (err) {
        console.error("Error fetching participants:", err);
        setParticipantsError("Failed to load participants");
        // Reset hasMoreParticipants on error to prevent infinite calls
        setHasMoreParticipants(false);
      } finally {
        setParticipantsLoading(false);
      }
    },
    [challengeSlug, challengeBatch]
  );

  // Load more feed posts
  const loadMoreFeed = useCallback(async () => {
    console.log(
      `loadMoreFeed called - hasMoreFeed: ${hasMoreFeed}, feedLoading: ${feedLoading}, currentPage: ${feedPage}`
    );
    if (!hasMoreFeed || feedLoading) {
      console.log("loadMoreFeed aborted - no more data or already loading");
      return;
    }
    console.log(`Loading feed page ${feedPage + 1}`);
    await fetchFeed(feedPage + 1, false);
  }, [feedPage, hasMoreFeed, feedLoading, fetchFeed]);

  // Load more participants
  const loadMoreParticipants = useCallback(async () => {
    console.log(
      `loadMoreParticipants called - hasMoreParticipants: ${hasMoreParticipants}, participantsLoading: ${participantsLoading}, currentPage: ${participantsPage}`
    );
    if (!hasMoreParticipants || participantsLoading) {
      console.log(
        "loadMoreParticipants aborted - no more data or already loading"
      );
      return;
    }
    console.log(`Loading participants page ${participantsPage + 1}`);
    await fetchParticipants(participantsPage + 1, false);
  }, [
    participantsPage,
    hasMoreParticipants,
    participantsLoading,
    fetchParticipants,
  ]);

  // Intersection observer for infinite scroll
  const feedObserver = useRef();
  const lastFeedElementRef = useCallback(
    (node) => {
      console.log("lastFeedElementRef called with node:", node);
      if (feedLoading) {
        console.log("Feed loading, skipping observer setup");
        return;
      }
      if (feedObserver.current) feedObserver.current.disconnect();
      feedObserver.current = new IntersectionObserver((entries) => {
        console.log(
          "Feed intersection observer triggered:",
          entries[0].isIntersecting,
          "hasMoreFeed:",
          hasMoreFeed,
          "feedLoading:",
          feedLoading
        );
        if (entries[0].isIntersecting && hasMoreFeed && !feedLoading) {
          console.log("Calling loadMoreFeed from intersection observer");
          loadMoreFeed();
        }
      });
      if (node) feedObserver.current.observe(node);
    },
    [feedLoading, hasMoreFeed, loadMoreFeed]
  );

  // Intersection observer for participants infinite scroll
  const participantsObserver = useRef();
  const lastParticipantElementRef = useCallback(
    (node) => {
      if (participantsLoading) return;
      if (participantsObserver.current)
        participantsObserver.current.disconnect();
      participantsObserver.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreParticipants &&
          !participantsLoading
        ) {
          loadMoreParticipants();
        }
      });
      if (node) participantsObserver.current.observe(node);
    },
    [participantsLoading, hasMoreParticipants, loadMoreParticipants]
  );

  // Reset feed pagination
  const resetFeedPagination = useCallback(() => {
    console.log("Resetting feed pagination");
    setFeedPosts([]);
    setFeedPage(1);
    setHasMoreFeed(true);
    setFeedError(null);
    setFeedInitialFetchAttempted(false);
  }, []);

  // Reset participants pagination
  const resetParticipantsPagination = useCallback(() => {
    console.log("Resetting participants pagination");
    setParticipants([]);
    setParticipantsPage(1);
    setHasMoreParticipants(true);
    setParticipantsError(null);
    setParticipantsInitialFetchAttempted(false);
  }, []);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);

      try {
        // First fetch challenge batch and current user (these don't depend on each other)
        await Promise.all([fetchChallengeBatch(), fetchCurrentUser()]);
      } catch (err) {
        setError("Failed to load challenge data");
        setLoading(false);
      }
    };

    if (challengeSlug && challengePurchaseId) {
      initializeData();
    }
  }, [
    challengeSlug,
    challengePurchaseId,
    fetchChallengeBatch,
    fetchCurrentUser,
  ]);

  // Initialize data that depends on challengeBatch
  useEffect(() => {
    const initializeBatchDependentData = async () => {
      if (!challengeBatch) return;

      try {
        // Now fetch data that depends on challengeBatch
        await Promise.all([fetchChallengeProgress(), fetchCheckpoints()]);
      } catch (err) {
        setError("Failed to load challenge data");
      } finally {
        setLoading(false);
      }
    };

    initializeBatchDependentData();
  }, [challengeBatch, fetchChallengeProgress, fetchCheckpoints]);

  // Load feed when tab changes to feed
  useEffect(() => {
    if (activeTab === "feed") {
      if (!feedInitialFetchAttempted && !feedLoading) {
        console.log("Loading initial feed data (first attempt)");
        setFeedInitialFetchAttempted(true);
        // Call fetchFeed directly to avoid dependency issues
        (async () => {
          try {
            setFeedLoading(true);
            setFeedError(null);

            console.log(`Fetching feed page 1, reset: true`);

            const response = await open_api_with_cookies.get(
              `challenge/${challengeSlug}/batch/${challengeBatch}/feed/?page=1`
            );

            console.log("Feed API response:", response.data);

            if (response.status === 200) {
              const newPosts = response.data.data || [];
              const hasNextPage = response.data.next_page !== null;

              console.log(
                `Received ${newPosts.length} posts, hasNextPage: ${hasNextPage}`
              );

              setFeedPosts(newPosts);
              setHasMoreFeed(hasNextPage);
              setFeedPage(1);
            }
          } catch (err) {
            console.error("Error fetching feed:", err);
            setFeedError("Failed to load feed");
            setHasMoreFeed(false);
          } finally {
            setFeedLoading(false);
          }
        })();
      }
    } else {
      // Reset feed when switching away from feed tab
      if (feedPosts.length > 0 || feedInitialFetchAttempted) {
        console.log("Switching away from feed tab, resetting feed pagination");
        resetFeedPagination();
      }
    }
  }, [
    activeTab,
    feedInitialFetchAttempted,
    feedLoading,
    feedPosts.length,
    challengeSlug,
    challengeBatch,
    resetFeedPagination,
  ]);

  // Load participants when tab changes to participants
  useEffect(() => {
    if (activeTab === "participants") {
      if (!participantsInitialFetchAttempted && !participantsLoading) {
        console.log("Loading initial participants data (first attempt)");
        setParticipantsInitialFetchAttempted(true);
        // Call fetchParticipants directly to avoid dependency issues
        (async () => {
          try {
            setParticipantsLoading(true);
            setParticipantsError(null);

            console.log(`Fetching participants page 1, reset: true`);

            const response = await open_api_with_cookies.get(
              `challenge/${challengeSlug}/batch/${challengeBatch}/participants/?page=1`
            );

            console.log("Participants API response:", response.data);

            if (response.status === 200) {
              const newParticipants = response.data.data || [];
              const hasNextPage = response.data.next_page !== null;

              console.log(
                `Received ${newParticipants.length} participants, hasNextPage: ${hasNextPage}`
              );

              setParticipants(newParticipants);
              setHasMoreParticipants(hasNextPage);
              setParticipantsPage(1);
            }
          } catch (err) {
            console.error("Error fetching participants:", err);
            setParticipantsError("Failed to load participants");
            setHasMoreParticipants(false);
          } finally {
            setParticipantsLoading(false);
          }
        })();
      }
    } else {
      // Reset participants when switching away from participants tab
      if (participants.length > 0 || participantsInitialFetchAttempted) {
        console.log(
          "Switching away from participants tab, resetting participants pagination"
        );
        resetParticipantsPagination();
      }
    }
  }, [
    activeTab,
    participantsInitialFetchAttempted,
    participantsLoading,
    participants.length,
    challengeSlug,
    challengeBatch,
    resetParticipantsPagination,
  ]);

  // Handle direct checkpoint access via URL
  useEffect(() => {
    if (urlCheckpointSlug && checkpoints.length > 0 && !modalOpen) {
      const checkpoint = checkpoints.find(
        (cp) => cp.checkpoint_slug === urlCheckpointSlug
      );
      if (checkpoint) {
        setSelectedCheckpoint(checkpoint);
        setModalOpen(true);
      }
    }
  }, [urlCheckpointSlug, checkpoints, modalOpen]);

  // Handle checkpoint start
  const handleCheckpointStart = (checkpoint) => {
    console.log("Starting checkpoint:", checkpoint);
    setSelectedCheckpoint(checkpoint);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCheckpoint(null);

    // If we came from a direct checkpoint URL, navigate back to main challenge page
    if (urlCheckpointSlug) {
      navigate(
        `/my-learning/challenge/${challengePurchaseId}/${challengeSlug}`,
        { replace: true }
      );
    }

    // Optionally refresh checkpoints data to get updated status
    fetchCheckpoints();
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return <ProgressSkeleton />;
  }

  if (error || !challengeData) {
    return (
      <div className={styles.errorContainer}>
        <h2>Oops! Something went wrong</h2>
        <p>{error || "Challenge not found"}</p>
        <button
          className={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.challengePage}>
      <Helmet>
        <title>{challengeData.title} - MyBurgo</title>
        <meta
          name="description"
          content={
            challengeData.hero_description?.replace(/<[^>]*>/g, "") ||
            challengeData.hero_title
          }
        />
        <meta
          property="og:title"
          content={`${challengeData.title} - MyBurgo`}
        />
        <meta property="og:description" content={challengeData.hero_title} />
        {challengeData.hero_image && (
          <meta property="og:image" content={challengeData.hero_image} />
        )}
      </Helmet>

      <HeroSection data={challengeData} />

      <div className={styles.contentSection}>
        {/* Desktop Tabs */}
        <div className={styles.tabsContainer}>
          <div className={styles.tabs}>
            {["progress", "feed", "participants"].map((tab) => (
              <button
                key={tab}
                className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ""}`}
                onClick={() => handleTabChange(tab)}
                aria-pressed={activeTab === tab}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.tabContent}>
          {activeTab === "progress" && (
            <div className={styles.progressContent}>
              <div className={styles.checkpointsList}>
                {checkpoints.map((checkpoint, index) => (
                  <CheckpointCard
                    key={checkpoint.id}
                    checkpoint={checkpoint}
                    index={index}
                    onStart={handleCheckpointStart}
                  />
                ))}
                {checkpoints.length === 0 && (
                  <div className={styles.emptyState}>
                    <h3>No checkpoints available yet</h3>
                    <p>Check back soon for new challenges to complete!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "feed" && (
            <div className={styles.feedContent}>
              {feedError ? (
                <div className={styles.feedError}>
                  <h3>Failed to load feed</h3>
                  <p>{feedError}</p>
                  <button
                    className={styles.retryButton}
                    onClick={() => fetchFeed(1, true)}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.feedList}>
                    {feedPosts.map((post, index) => (
                      <FeedPost
                        key={post.id}
                        post={post}
                        index={index}
                        ref={
                          index === feedPosts.length - 1
                            ? lastFeedElementRef
                            : null
                        }
                        onUpdatePost={handleFeedPostUpdate}
                        currentUserId={currentUserId}
                        showToast={showToast}
                      />
                    ))}
                  </div>

                  {feedLoading && (
                    <div className={styles.feedLoadingMore}>
                      {[...Array(3)].map((_, i) => (
                        <FeedPostSkeleton key={i} />
                      ))}
                    </div>
                  )}

                  {feedPosts.length === 0 && !feedLoading && (
                    <div className={styles.emptyFeed}>
                      <h3>No posts yet</h3>
                      <p>Be the first to share your progress!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "participants" && (
            <div className={styles.participantsContent}>
              {participantsError ? (
                <div className={styles.participantsError}>
                  <h3>Failed to load participants</h3>
                  <p>{participantsError}</p>
                  <button
                    className={styles.retryButton}
                    onClick={() => fetchParticipants(1, true)}
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  <div className={styles.participantsList}>
                    {participants.map((participant, index) => (
                      <ParticipantCard
                        key={participant.id}
                        participant={participant}
                        index={index}
                        ref={
                          index === participants.length - 1
                            ? lastParticipantElementRef
                            : null
                        }
                      />
                    ))}
                  </div>

                  {participantsLoading && (
                    <div className={styles.participantsLoadingMore}>
                      {[...Array(3)].map((_, i) => (
                        <ParticipantSkeleton key={i} />
                      ))}
                    </div>
                  )}

                  {participants.length === 0 && !participantsLoading && (
                    <div className={styles.emptyParticipants}>
                      <h3>No participants yet</h3>
                      <p>Be the first to join this challenge!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className={styles.mobileBottomNav}>
        <div className={styles.mobileBottomNavTabs}>
          {["progress", "feed", "participants"].map((tab) => (
            <button
              key={tab}
              className={`${styles.mobileTab} ${activeTab === tab ? styles.activeMobileTab : ""}`}
              onClick={() => handleTabChange(tab)}
              aria-pressed={activeTab === tab}
              aria-label={`${tab.charAt(0).toUpperCase() + tab.slice(1)} tab`}
            >
              <span className={styles.mobileTabIcon}>{getTabIcon(tab)}</span>
              <span className={styles.mobileTabLabel}>{tab}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Toast Container */}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Checkpoint Detail Modal */}
      <CheckpointDetailModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        checkpointSlug={selectedCheckpoint?.checkpoint_slug}
        challengeSlug={challengeSlug}
        challengeBatch={challengeBatch}
        challengePurchaseId={challengePurchaseId}
      />
    </div>
  );
};

export default ChallengePage;
